import { Injectable, Signal, computed, signal } from '@angular/core';

export interface RecentLinkEntry {
	portalLinkID: number;
	lastOpenedAt: number;
}

/**
 * Tracks portal-link opens in localStorage so the launcher can surface a "Recent" list.
 * Stores only IDs + timestamps — never a snapshot of the link — so name/icon/favorite
 * stay current when IDs are resolved against the live entity store by the caller.
 */
@Injectable({ providedIn: 'root' })
export class RecentLinksService {
	static readonly STORAGE_KEY = 'portal.recentLinks';
	static readonly MAX_ENTRIES = 12;
	// Entries not opened within this window drop off, so "Recent" stays recent-in-time and
	// not just last-N. Pruned lazily (on read + on the next record), not on a timer.
	static readonly MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

	private readonly entriesSignal = signal<RecentLinkEntry[]>(this.read());

	/** Recent opens, most-recent-first, deduped by portalLinkID, capped at MAX_ENTRIES. */
	readonly entries: Signal<RecentLinkEntry[]> = this.entriesSignal.asReadonly();

	/** Raw stored count so callers can show/hide the Recent area when empty. */
	readonly count = computed(() => this.entriesSignal().length);

	/** Records a link open. Synchronous + best-effort so it never blocks navigation. */
	record(portalLinkID: number): void {
		if (!Number.isFinite(portalLinkID)) {
			return;
		}

		const now = Date.now();
		const next = [
			{ portalLinkID, lastOpenedAt: now },
			...this.entriesSignal().filter((e) => e.portalLinkID !== portalLinkID && this.isFresh(e, now))
		].slice(0, RecentLinksService.MAX_ENTRIES);

		this.entriesSignal.set(next);
		this.write(next);
	}

	private isFresh(entry: RecentLinkEntry, now: number): boolean {
		return now - entry.lastOpenedAt <= RecentLinksService.MAX_AGE_MS;
	}

	private read(): RecentLinkEntry[] {
		try {
			const raw = localStorage.getItem(RecentLinksService.STORAGE_KEY);
			if (!raw) {
				return [];
			}
			const parsed = JSON.parse(raw);
			if (!Array.isArray(parsed)) {
				return [];
			}
			const now = Date.now();
			return parsed
				.filter((e): e is RecentLinkEntry => !!e && typeof e.portalLinkID === 'number' && typeof e.lastOpenedAt === 'number')
				.filter((e) => this.isFresh(e, now))
				.slice(0, RecentLinksService.MAX_ENTRIES);
		} catch {
			// Malformed / unavailable storage — recent list is best-effort, start empty.
			return [];
		}
	}

	private write(entries: RecentLinkEntry[]): void {
		try {
			localStorage.setItem(RecentLinksService.STORAGE_KEY, JSON.stringify(entries));
		} catch {
			// Storage full / unavailable — ignore; the in-memory signal still works this session.
		}
	}
}
